import { useRef, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import type { SortDir } from '../api/searchTypes';
import { columnsBadge } from '../state/traderColumnsPref';
import type { TraderColumnsView } from '../state/useTraderColumns';
import { SortableHeaderCell } from './SortableHeaderCell';
import { TraderColumnsMenu } from './TraderColumnsMenu';
import {
    TRADER_CELL_WIDTH,
    TRADER_COLUMN_GROUPS,
    groupDividerSx,
    groupStarts,
} from './traderColumns';

const HEADER_DIVIDER_SX = groupDividerSx();

export interface TraderResultsHeaderProps {
    sort: string;
    dir: SortDir;
    sortableKeys: readonly string[];
    sortInfo: Record<string, string>;
    onSort: (key: string) => void;
    view: TraderColumnsView;
    gridTemplate: string;
}

const GROUP_LABEL_SX = {
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: 'text.secondary',
    textTransform: 'uppercase',
} as const;

// Spec 4.3: a group row (the Columns button in the pinned corner) over the
// title row; every title can hide its column.
export function TraderResultsHeader({
    sort,
    dir,
    sortableKeys,
    sortInfo,
    onSort,
    view,
    gridTemplate,
}: TraderResultsHeaderProps) {
    const theme = useTheme();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    // Hiding a column removes the focused hide button; focus goes here.
    const columnsButton = useRef<HTMLButtonElement>(null);
    const starts = groupStarts(view.columns);
    const pinned = {
        position: 'sticky',
        left: 0,
        zIndex: 3,
        alignSelf: 'stretch',
        // Cover the row's left padding, as the pinned cell of the rows does.
        ml: -1.25,
        pl: 1.25,
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.default',
        boxShadow: `1px 0 0 ${theme.palette.divider}`,
    } as const;
    const grid = {
        display: 'grid',
        gridTemplateColumns: gridTemplate,
        alignItems: 'center',
        columnGap: 1,
        px: 1.25,
    } as const;
    const groups = TRADER_COLUMN_GROUPS.map(group => ({
        ...group,
        span: view.columns.filter(c => c.group === group.key).length,
    })).filter(group => group.span > 0);

    return (
        <Box
            sx={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                bgcolor: 'background.default',
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            <Box sx={{ ...grid, height: 26 }}>
                <Box sx={pinned}>
                    <Button
                        ref={columnsButton}
                        size="small"
                        variant="outlined"
                        aria-haspopup="dialog"
                        aria-expanded={anchor !== null}
                        onClick={e => setAnchor(e.currentTarget)}
                        startIcon={
                            <ViewColumnIcon
                                sx={{ fontSize: '14px !important' }}
                            />
                        }
                        sx={{
                            height: 22,
                            fontSize: '11px',
                            textTransform: 'none',
                            py: 0,
                            px: 1,
                        }}
                    >
                        Columns
                        <Box
                            component="span"
                            sx={{
                                ml: 0.75,
                                fontSize: '10px',
                                color: 'text.secondary',
                            }}
                        >
                            {columnsBadge(view.mode, view.columns.length)}
                        </Box>
                    </Button>
                </Box>
                {groups.map((group, i) => (
                    <Box
                        key={group.key}
                        sx={{
                            gridColumn: `span ${group.span}`,
                            alignSelf: 'stretch',
                            display: 'flex',
                            alignItems: 'flex-end',
                            pb: '2px',
                            ...(i > 0 ? HEADER_DIVIDER_SX : {}),
                        }}
                    >
                        {/* Sticks to the visible area's left edge while the group scrolls past. */}
                        <Typography
                            component="span"
                            sx={{
                                ...GROUP_LABEL_SX,
                                display: 'inline-block',
                                position: 'sticky',
                                left: TRADER_CELL_WIDTH + 16,
                            }}
                        >
                            {group.label}
                        </Typography>
                    </Box>
                ))}
            </Box>
            <Box sx={{ ...grid, height: 26 }}>
                <Box sx={pinned}>
                    <Typography
                        sx={{
                            fontSize: '10px',
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                        }}
                    >
                        TRADER
                    </Typography>
                </Box>
                {view.columns.map(column => {
                    const sortable = Boolean(
                        column.sort && sortableKeys.includes(column.sort)
                    );
                    const info =
                        (column.sort && sortInfo[column.sort]) ||
                        column.info ||
                        '';
                    return (
                        <Box
                            key={column.key}
                            sx={{
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center',
                                alignSelf: 'stretch',
                                minWidth: 0,
                                ...(starts.has(column.key)
                                    ? HEADER_DIVIDER_SX
                                    : {}),
                                '& .hide-column': { opacity: 0 },
                                '&:hover .hide-column, & .hide-column:focus-visible':
                                    { opacity: 1 },
                                // No hover on touch screens: always show it.
                                '@media (hover: none)': {
                                    '& .hide-column': { opacity: 1 },
                                },
                            }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <SortableHeaderCell
                                    label={column.label}
                                    align="left"
                                    sortKey={column.sort}
                                    sortable={sortable}
                                    active={sortable && column.sort === sort}
                                    dir={dir}
                                    info={info}
                                    unsortableTitle={
                                        info ? `${info} Not sortable.` : ''
                                    }
                                    onSort={onSort}
                                />
                            </Box>
                            <IconButton
                                className="hide-column"
                                size="small"
                                aria-label={`Hide ${column.label}`}
                                onClick={() => {
                                    view.hide(column.key);
                                    columnsButton.current?.focus();
                                }}
                                // Laid over the end of the title, so it takes
                                // no width; it only shows (and so can only be
                                // hit knowingly) on hover or keyboard focus.
                                sx={{
                                    p: '1px',
                                    position: 'absolute',
                                    right: 0,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    zIndex: 1,
                                    bgcolor: 'background.default',
                                    '&:hover': {
                                        bgcolor: 'background.default',
                                        color: 'error.main',
                                    },
                                }}
                            >
                                <CloseIcon sx={{ fontSize: 10 }} />
                            </IconButton>
                        </Box>
                    );
                })}
            </Box>
            <TraderColumnsMenu
                anchor={anchor}
                onClose={() => setAnchor(null)}
                view={view}
                sortableKeys={sortableKeys}
            />
        </Box>
    );
}
